import { ResizeObserver } from '@juggle/resize-observer';
import { useEffect, useState } from 'react';
import styles from './styles.css.js';
import { browserScrollbarSize, getOverflowParents, getOverflowParentDimensions } from '../internal/utils/scrollable-containers';
import { supportsStickyPosition } from '../internal/utils/dom';
var updatePosition = function (tableEl, wrapperEl, scrollbarEl, consideredFooterHeight) {
    if (!tableEl || !scrollbarEl || !wrapperEl) {
        return;
    }
    // parent is either some container or document itself
    var parent = getOverflowParentDimensions(wrapperEl)[0];
    var parentBottom = parent.top + parent.height;
    // table bottom is visible when
    // 1. table bottom reached end of the window
    // 2. table bottom is not overlapped by footer
    var _a = tableEl.getBoundingClientRect(), tableBottom = _a.bottom, tableWidth = _a.width;
    var wrapperWidth = wrapperEl.getBoundingClientRect().width;
    //scrollbar correction is needed for
    // #1 when scrollbars are constantly visible,
    // we want no visible break when switching between fake and real scrollbars
    // #2 when scrollbars are visible only on scrolling and half transparent (on mac)
    // we want to avoid any overlap between fake and real scrollbar
    // using 15 px as a height of transparent scrollbar on mac
    var scrollbarHeight = browserScrollbarSize().height;
    var scrollBarCorrection = scrollbarHeight > 0 ? scrollbarHeight : -15 / 2;
    var tableBottomIsVisible = parentBottom - consideredFooterHeight >= tableBottom + scrollBarCorrection;
    var areaIsScrollable = tableWidth > wrapperWidth;
    if (tableBottomIsVisible || !areaIsScrollable) {
        scrollbarEl.classList.remove(styles['sticky-scrollbar-visible']);
    }
    else {
        // when scrollbar is not displayed scrollLeft property cannot be set by useScrollSync
        // that's why syncing it separately
        if (!scrollbarEl.classList.contains(styles['sticky-scrollbar-visible'])) {
            requestAnimationFrame(function () {
                scrollbarEl.scrollLeft = wrapperEl.scrollLeft;
            });
        }
        scrollbarEl.classList.add(styles['sticky-scrollbar-visible']);
    }
};
export function useStickyScrollbar(scrollbarRef, scrollbarContentRef, tableRef, wrapperRef, footerHeight) {
    var _a = useState(false), isInContainer = _a[0], setIsInContainer = _a[1];
    var consideredFooterHeight = isInContainer ? 0 : footerHeight;
    useEffect(function () {
        if (supportsStickyPosition()) {
            var scrollHandler_1 = function () {
                return updatePosition(tableRef.current, wrapperRef.current, scrollbarRef.current, consideredFooterHeight);
            };
            window.addEventListener('scroll', scrollHandler_1, true);
            return function () {
                window.removeEventListener('scroll', scrollHandler_1, true);
            };
        }
    }, [scrollbarRef, tableRef, wrapperRef, consideredFooterHeight]);
    useEffect(function () {
        if (supportsStickyPosition()) {
            updatePosition(tableRef.current, wrapperRef.current, scrollbarRef.current, consideredFooterHeight);
            var wrapperEl_1 = wrapperRef.current;
            if (wrapperEl_1) {
                var parents = getOverflowParents(wrapperEl_1);
                setIsInContainer(parents.length > 0);
            }
        }
        // This effect is supposed to run only on initial render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        if (supportsStickyPosition() && tableRef.current) {
            var observer_1 = new ResizeObserver(function (entries) {
                if (scrollbarContentRef.current) {
                    scrollbarContentRef.current.style.width = entries[0].borderBoxSize[0].inlineSize + "px";
                    updatePosition(tableRef.current, wrapperRef.current, scrollbarRef.current, consideredFooterHeight);
                }
            });
            observer_1.observe(tableRef.current);
            return function () {
                observer_1.disconnect();
            };
        }
    }, [scrollbarContentRef, scrollbarRef, tableRef, wrapperRef, consideredFooterHeight]);
    // Sticky scrollbar relies on css sticky position.
    // Disabling functionality if sticky position is not supported
    if (!supportsStickyPosition()) {
        return;
    }
    var scrollbarEl = scrollbarRef.current;
    var scrollbarContentEl = scrollbarContentRef.current;
    var tableEl = tableRef.current;
    var wrapperEl = wrapperRef.current;
    var scrollbarHeight = browserScrollbarSize().height;
    if (scrollbarHeight && scrollbarEl && scrollbarContentEl) {
        scrollbarEl.style.height = scrollbarHeight + "px";
        scrollbarContentEl.style.height = scrollbarHeight + "px";
    }
    if (tableEl && wrapperEl && scrollbarContentEl && scrollbarEl) {
        var tableElRect = tableEl.getBoundingClientRect();
        scrollbarContentEl.style.width = tableElRect.width + "px";
        // when using sticky scrollbars in containers
        // we agreed to ignore dynamic bottom calculations for footer overlap
        scrollbarEl.style.bottom = isInContainer ? '0px' : footerHeight + "px";
    }
}
//# sourceMappingURL=use-sticky-scrollbar.js.map